/**
 * Medivigil - Medication Normalization Engine
 * Converts medication inputs to canonical generic names
 * 
 * DETERMINISTIC: Same input always produces same output
 * NO AI/LLM: Pure rule-based matching
 */

import drugAliases from '../data/drugAliases.json';

/**
 * Normalizes a medication name to its canonical generic form
 * @param {string} medicationInput - Raw medication input (brand name, generic, with/without dosage)
 * @returns {object} - { normalized: string, status: 'MATCHED' | 'UNCLASSIFIED', original: string }
 */
export function normalizeMedication(medicationInput) {
  if (!medicationInput || typeof medicationInput !== 'string') {
    return {
      normalized: null,
      status: 'UNCLASSIFIED',
      original: medicationInput,
      message: 'Invalid input'
    };
  }

  // Step 1: Clean the input
  const cleaned = cleanMedicationInput(medicationInput);
  
  // Step 2: Search for match in aliases
  const match = findAliasMatch(cleaned);
  
  if (match) {
    return {
      normalized: match.generic,
      status: 'MATCHED',
      original: medicationInput,
      cleanedInput: cleaned
    };
  }
  
  return {
    normalized: null,
    status: 'UNCLASSIFIED',
    original: medicationInput,
    cleanedInput: cleaned,
    message: 'Medication not recognized. Manual review required.'
  };
}

/**
 * Cleans medication input for matching
 * - Converts to lowercase
 * - Removes dosage numbers and units
 * - Trims whitespace
 * - Removes common suffixes
 */
function cleanMedicationInput(input) {
  let cleaned = input.toLowerCase().trim();
  
  // Remove dosage patterns (e.g., "5mg", "500 mg", "10mg/ml", "2.5 mg")
  cleaned = cleaned.replace(/\d+\.?\d*\s*(mg|mcg|g|ml|%|units?|iu)/gi, '');
  
  // Remove common formulation descriptors
  cleaned = cleaned.replace(/\b(tablet|capsule|injection|solution|cream|ointment|patch|er|xr|sr|cr|dr|hcl|sodium|potassium)\b/gi, '');
  
  // Preserve (systemic) as a searchable keyword before stripping other parens
  cleaned = cleaned.replace(/\(systemic\)/gi, 'systemic');
  // Remove remaining parenthetical content
  cleaned = cleaned.replace(/\([^)]*\)/g, '');
  
  // Remove special characters except hyphens and spaces
  cleaned = cleaned.replace(/[^\w\s-]/g, '');
  
  // Collapse multiple spaces
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
}

/**
 * Searches for a medication match in the alias database.
 * Uses best-score selection so a longer/more-specific alias always wins over a
 * shorter one that happens to appear first in the JSON:
 *   exact match  (score 1000 + alias length)  >
 *   word-boundary match (500 + alias length)   >
 *   contains match      (input length)
 */
function findAliasMatch(cleanedInput) {
  const aliases = drugAliases.aliases;
  let bestMatch = null;
  let bestScore = -1;

  for (const [genericName, data] of Object.entries(aliases)) {
    for (const alias of data.aliases) {
      const al = alias.toLowerCase();
      let score = -1;

      if (cleanedInput === al) {
        score = 1000 + al.length;
      } else {
        const aliasRegex = new RegExp(`\\b${escapeRegex(al)}\\b`);
        if (aliasRegex.test(cleanedInput)) {
          score = 500 + al.length;
        } else if (al.includes(cleanedInput) && cleanedInput.length >= 4) {
          score = cleanedInput.length;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = data;
      }
    }
  }

  return bestMatch;
}

/**
 * Escapes special regex characters
 */
function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Normalizes multiple medications
 * @param {string[]} medications - Array of medication inputs
 * @returns {object[]} - Array of normalization results
 */
export function normalizeMedications(medications) {
  if (!Array.isArray(medications)) {
    return [];
  }
  
  return medications.map(med => normalizeMedication(med));
}

/**
 * Gets normalization summary statistics
 */
export function getNormalizationSummary(results) {
  const matched = results.filter(r => r.status === 'MATCHED');
  const unclassified = results.filter(r => r.status === 'UNCLASSIFIED');
  
  return {
    total: results.length,
    matched: matched.length,
    unclassified: unclassified.length,
    matchedMedications: matched.map(r => r.normalized),
    unclassifiedInputs: unclassified.map(r => r.original)
  };
}

export default {
  normalizeMedication,
  normalizeMedications,
  getNormalizationSummary
};
